import type { CurationDecision, CurationDecisionKind, Extraction } from "../../../api/knowledgeData/knowledgeDataDecoders";

/**
 * The provenance of one extraction result, as rows the panel shows: the Thread entry that
 * holds it, the versions the step used, the LLM and the people (knowledge-data.md K-2, K-3).
 * Every value is a recorded fact; nothing is derived from what the LLM wrote. Each row is
 * shown by what it names (F155: the Run, the step, the review), and keeps the recorded ID,
 * which Technical details show; a row that is an ID only (a digest, a step key) is shown
 * there alone. Each statement's exact source is its span, which the result's view draws on
 * the text (H-21). Pure, so the panel only renders.
 */

/** What a row names, which the panel shows by its name; `null`: the recorded ID is all there is. */
export type ProvenanceShown =
  | { readonly kind: "entry"; readonly threadId: string; readonly entry: number }
  | { readonly kind: "item"; readonly reference: string }
  | { readonly kind: "modelVersion"; readonly reference: string }
  | { readonly kind: "person"; readonly userId: string }
  | { readonly kind: "review"; readonly reviewer: string }
  | { readonly kind: "curation"; readonly curator: string; readonly decision: CurationDecisionKind | "carried_forward" }
  | { readonly kind: "generation"; readonly generation: "agent_turn" | "carried_forward" }
  | { readonly kind: "time"; readonly at: string }
  | { readonly kind: "text"; readonly text: string };

export interface ProvenanceRow {
  /** The row's label key under `data.provenance.rows`. */
  readonly key: ProvenanceRowKey;
  readonly shown: ProvenanceShown | null;
  /** The value as recorded, for Technical details. */
  readonly recorded: string;
}

export type ProvenanceRowKey =
  | "threadEntry"
  | "generatedBy"
  | "stepKey"
  | "workflow"
  | "step"
  | "prompt"
  | "contentVersion"
  | "corpusVersion"
  | "model"
  | "agent"
  | "route"
  | "host"
  | "requestedBy"
  | "started"
  | "finished"
  | "inputDigest"
  | "referencesRead"
  | "reviewedOutput"
  | "curator"
  | "mapping";

export function resultProvenance(extraction: Extraction): readonly ProvenanceRow[] {
  return [
    {
      key: "threadEntry",
      shown: { kind: "entry", threadId: extraction.threadId, entry: extraction.outputEntry },
      recorded: `${extraction.threadId}#${String(extraction.outputEntry)}`,
    },
    ...generationRows(extraction),
    ...curationRows(extraction.decisions),
    { key: "mapping", shown: null, recorded: extraction.mappingVersion },
  ];
}

const row = (key: ProvenanceRowKey, shown: ProvenanceShown | null, recorded: string): ProvenanceRow => ({ key, shown, recorded });

/** A row for a value that may not be recorded: none without it. */
function optional(key: ProvenanceRowKey, recorded: string | null, shown: (value: string) => ProvenanceShown | null): ProvenanceRow[] {
  return recorded === null ? [] : [row(key, shown(recorded), recorded)];
}

const item = (reference: string): ProvenanceShown => ({ kind: "item", reference });
const text = (value: string): ProvenanceShown => ({ kind: "text", text: value });
const time = (at: string): ProvenanceShown => ({ kind: "time", at });
const person = (userId: string): ProvenanceShown => ({ kind: "person", userId });
const idOnly = (): null => null;

function generationRows(extraction: Extraction): ProvenanceRow[] {
  const generation = extraction.generation;
  if (generation.kind === "carried_forward") {
    return [
      row("generatedBy", { kind: "generation", generation: "carried_forward" }, generation.kind),
      row("reviewedOutput", entryOf(generation.revises), generation.revises),
      row("curator", { kind: "curation", curator: generation.curator, decision: "carried_forward" }, generation.curator),
      row("finished", time(generation.at), generation.at),
    ];
  }
  if (generation.kind === "human_review") {
    return [
      row("generatedBy", { kind: "review", reviewer: generation.reviewer }, generation.reviewer),
      row("stepKey", null, generation.stepKey),
      row("step", item(generation.step), generation.step),
      ...optional("reviewedOutput", generation.reviewedOutput, entryOf),
    ];
  }
  const ran = generation.ran;
  const agent = ran === null ? null : [ran.name, ran.version].filter((part) => part !== null).join(" ");
  const route = `${generation.route.kind}: ${generation.route.name}`;
  return [
    row("generatedBy", { kind: "generation", generation: "agent_turn" }, generation.kind),
    ...optional("stepKey", generation.stepKey, idOnly),
    ...optional("workflow", generation.workflow, item),
    ...optional("step", generation.step, item),
    ...optional("prompt", generation.prompt, item),
    row("contentVersion", { kind: "modelVersion", reference: generation.contentVersion }, generation.contentVersion),
    ...optional("corpusVersion", generation.corpusVersion, idOnly),
    ...optional("model", ran?.model ?? generation.route.requestedModel ?? null, text),
    ...optional("agent", agent, text),
    row("route", text(route), route),
    row("host", person(generation.host), generation.host),
    ...optional("requestedBy", generation.requestedBy, person),
    ...optional("started", generation.startedAt, time),
    ...optional("finished", generation.finishedAt, time),
    row("inputDigest", null, generation.inputDigest),
    row("referencesRead", text(String(generation.referencesRead.length)), generation.referencesRead.join(" ")),
  ];
}

function curationRows(decisions: readonly CurationDecision[]): ProvenanceRow[] {
  return decisions.map((decision) => row("curator", { kind: "curation", curator: decision.curator, decision: decision.kind }, decision.curator));
}

/** The entry a `<thread>#<n>` names; a carried revision (`<thread>#<n>/r<k>`) lives in its output entry. */
function entryOf(reference: string): ProvenanceShown | null {
  const [threadId, entry = ""] = reference.split("#");
  const number = Number(entry.split("/r")[0]);
  return threadId === undefined || threadId === "" || !Number.isInteger(number) || number < 1 ? null : { kind: "entry", threadId, entry: number };
}
